<template>
  <b-field :label="$t('NotificationGroup')" >
    <b-autocomplete
      expanded
      :placeholder="canEdit === true ? $t('SelectNotificationGroup') : ''"
      v-model="groupText"
      icon-pack="fas"
      icon="search"
      :data="filteredGroupList"
      :custom-formatter="formatGroup"
      :open-on-focus="true"
      @select="selectGroup"
      @typing="setFilteredGroupList"
      :disabled="canEdit === false "
      data-test-id="selectNotificationGroup"
      ref="groupAutocomplete"
    ></b-autocomplete>
  </b-field>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import notificationService from '@/services/notificationService'
import { useAppStore } from '@/stores/app'
import type { NotificationGroup } from '@/stores/app'

interface GroupOption {
  groupId: number
  groupName: string
  groupDisplay: string
}

// Define props
const props = defineProps<{
  modelValue?: number | null
  canEdit?: boolean
}>()

// Define emits
const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
}>()

// Composables
const store = useAppStore()

// Reactive data with proper typing
const selectedNotificationGroupId = ref<number>(props.modelValue || 0)
const groupText = ref<string>('')
const groupAutocomplete = ref<{ setSelected: (value: NotificationGroup) => void } | null>(null)

// Filtered groups list
const filteredGroupList = ref<GroupOption[]>([])

// Computed properties
const groupList = computed(() => store.getNotificationGroups)

const selectedValue = computed(() => {
  if (groupList.value && props.modelValue) {
    return groupList.value.find((grp) => grp.groupId === props.modelValue) || null
  }
  return null
})

// Methods
const formatGroup = (group: NotificationGroup | GroupOption): string => {
  if (groupList.value && group) {
    const groupDetails = groupList.value.find(
      (grp) => grp.groupId === group.groupId
    )
    if (groupDetails) {
      return `${groupDetails.groupId} ${groupDetails.groupName || ''}`
    }
  }
  return ''
}

const setFilteredGroupList = (text: string): void => {
  if (text.trim() === '') {
    selectedNotificationGroupId.value = 0
  }

  if (groupList.value) {
    const mappedData: GroupOption[] = groupList.value
      .map((element) => ({
        groupId: element.groupId,
        groupName: element.groupName || '',
        groupDisplay: element.groupId.toString().concat(' ').concat(element.groupName || '')
      }))
      .filter((option) =>
        option.groupName
          .concat(option.groupId.toString())
          .toLowerCase()
          .indexOf(text.toLowerCase()) >= 0
      )
    filteredGroupList.value = mappedData
  }
}

const selectGroup = (option: GroupOption | null): void => {
  selectedNotificationGroupId.value = option ? option.groupId : 0
}

// Lifecycle hooks
onMounted(async () => {
  if (groupList.value === null) {
    await notificationService.getNotificationGroups()
  }
  
  if (selectedValue.value) {
    groupText.value = formatGroup(selectedValue.value)
    // Set selected value in autocomplete after next tick
    await nextTick()
    if (groupAutocomplete.value && selectedValue.value) {
      groupAutocomplete.value.setSelected(selectedValue.value)
    }
  }
  
  if (groupList.value) {
    const mappedData = groupList.value.map((element) => ({
      groupId: element.groupId,
      groupName: element.groupName || '',
      groupDisplay: element.groupId.toString().concat(' ').concat(element.groupName || '')
    }))
    filteredGroupList.value = mappedData
  }
})

// Watchers
watch(selectedNotificationGroupId, (newValue) => {
  emit('update:modelValue', newValue)
})

watch(() => props.modelValue, (newValue) => {
  selectedNotificationGroupId.value = newValue || 0
  if (selectedValue.value) {
    groupText.value = formatGroup(selectedValue.value)
  }
})
</script>
